/**
 * LindaCrossSellCard
 * ==================
 * Iter 514 · Sep 26 2026 · Karine ask —
 * "Our NP Linda is starting to offer Botox and filler treatments on
 *  Thursday October 8th so if Karine is booked solid on Mondays and
 *  Fridays when clients are looking, offer them an appointment with
 *  Linda."
 *
 * Renders next to `NearestSlotsCard` in the booking-flow empty state.
 * Fetches `/api/booking/linda-cross-sell` and, when eligible, shows a
 * discreet "Meet Linda · Thursday openings" pill that opens a modal
 * with Linda's bio + her next 3 Thursday openings. Tapping a slot
 * kicks the client into the booking flow under Linda's calendar via
 * `?provider=linda&date=...&time=...`.
 *
 * Renders NULL when:
 *   • service_name isn't Botox/Filler
 *   • Karine has openings in the next 7 days
 *   • Linda has no Thursday slots (edge case, don't surface an empty
 *     modal)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Calendar, Clock, X, ArrowRight, Award } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

function _fmtTime12(hhmm) {
  if (!hhmm || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

function _fmtDayLabel(iso, isFr = false) {
  try {
    const [Y, M, D] = iso.split("-").map((n) => parseInt(n, 10));
    const d = new Date(Y, M - 1, D);
    const wk = d.toLocaleDateString(isFr ? "fr-CA" : "en-US", { weekday: "long" });
    const md = d.toLocaleDateString(isFr ? "fr-CA" : "en-US", { month: "short", day: "numeric" });
    return `${wk}, ${md}`;
  } catch {
    return iso;
  }
}

export default function LindaCrossSellCard({
  serviceName,
  serviceVariationId = null,
  locationId,
  preferredDate,
  isFr = false,
  onPickLindaSlot, // (slot) => void — parent redirects/updates booking state
}) {
  const [data, setData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fire the cross-sell check whenever the underlying context changes.
  useEffect(() => {
    let cancelled = false;
    if (!serviceName || !locationId) {
      setData(null);
      return;
    }
    // Quick client-side guard to avoid hitting the API for services that
    // definitely aren't Botox/Filler (saves the ~1s Square round-trip).
    const n = String(serviceName || "").toLowerCase();
    if (!(n.includes("botox") || n.includes("filler") || n.includes("nuceiva") || n.includes("dysport"))) {
      setData(null);
      return;
    }

    const params = new URLSearchParams({
      location_id: String(locationId),
      service_name: serviceName,
    });
    if (serviceVariationId) params.set("service_variation_id", serviceVariationId);
    if (preferredDate) params.set("preferred_date", preferredDate);

    axios
      .get(`${API}/api/booking/linda-cross-sell?${params.toString()}`, { timeout: 45000 })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [serviceName, serviceVariationId, locationId, preferredDate]);

  const eligible = Boolean(data && data.eligible && data.linda && (data.linda.thursdays || []).length);
  const totalSlots = useMemo(() => {
    if (!eligible) return 0;
    return (data.linda.thursdays || []).reduce((n, t) => n + (t.times || []).length, 0);
  }, [eligible, data]);

  if (!eligible) return null;
  const linda = data.linda;

  const handlePick = (thu, hhmm, variationId) => {
    setModalOpen(false);
    if (onPickLindaSlot) {
      onPickLindaSlot({
        provider: "linda",
        location_id: linda.location_id,
        date: thu.date,
        day_name: thu.day_name,
        start_time: hhmm,
        variation_id: variationId,
      });
      return;
    }
    // Fallback: navigate to booking flow with query params.
    const p = new URLSearchParams({
      provider: "linda",
      location: linda.location_id || "3",
      date: thu.date,
      time: hhmm,
    });
    if (variationId) p.set("variation_id", variationId);
    window.location.href = `/book?${p.toString()}`;
  };

  return (
    <>
      {/* ── Compact card in the empty state ── */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="linda-cross-sell-card"
        className="w-full text-left rounded-xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-500/15 via-teal-400/5 to-transparent p-4 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/20 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-emerald-400/70 shrink-0 bg-emerald-900/30">
            <img
              src={linda.photo_url}
              alt="Linda Lafrance NP"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Award className="w-3 h-3 text-emerald-300" />
              <p className="text-[10px] uppercase tracking-[2px] font-black text-emerald-300">
                {isFr ? "Aussi disponible" : "Also available"}
              </p>
            </div>
            <p className="text-white text-sm font-bold leading-tight mt-0.5">
              {isFr ? "Prenez rendez-vous avec Linda, NP" : "Book with Linda, NP"}
            </p>
            <p className="text-emerald-100/80 text-xs leading-snug mt-0.5">
              {isFr
                ? `${totalSlots} créneau${totalSlots === 1 ? "" : "x"} le jeudi à Ottawa — ${_fmtDayLabel(linda.thursdays[0].date, isFr)}`
                : `${totalSlots} Thursday opening${totalSlots === 1 ? "" : "s"} at Ottawa — starts ${_fmtDayLabel(linda.thursdays[0].date, isFr)}`}
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-emerald-300 shrink-0" />
        </div>
      </button>

      {/* ── Modal: bio + Thursday slots ── */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) setModalOpen(false); }}>
        <DialogContent
          className="max-w-lg w-[95vw] p-0 overflow-hidden border-emerald-500/40 max-h-[90vh] flex flex-col"
          style={{ background: "#0B111F", color: "white" }}
          data-testid="linda-cross-sell-modal"
        >
          {/* Header (hero) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white"
              data-testid="linda-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="relative bg-gradient-to-br from-emerald-900/60 via-teal-800/40 to-zinc-900 px-5 pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-emerald-400/70 shrink-0 bg-emerald-900/40">
                  <img
                    src={linda.photo_url}
                    alt="Linda Lafrance NP"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[3px] font-black text-emerald-300 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> {isFr ? "Rencontrez" : "Meet"}
                  </p>
                  <h2 className="text-white text-xl font-bold leading-tight" data-testid="linda-modal-name">
                    {linda.name}
                  </h2>
                  <p className="text-emerald-200 text-xs italic leading-tight mt-0.5">
                    {linda.credentials}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 overflow-y-auto flex-1">
            {/* Why we're offering Linda */}
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3 py-2.5 mb-4">
              <p className="text-emerald-100 text-xs leading-relaxed">
                {isFr
                  ? `Karine est complète cette semaine pour ${data.service_name}. Linda est parfaitement qualifiée pour ce traitement et prend des rendez-vous à Ottawa chaque jeudi.`
                  : `Karine is fully booked next week for ${data.service_name}. Linda is a perfect fit for this treatment and sees clients at our Ottawa clinic every Thursday.`}
              </p>
            </div>

            {/* Bio */}
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              {linda.bio_short}
            </p>

            {/* Thursday openings */}
            <div className="mb-2">
              <p className="text-[10px] uppercase tracking-[2px] font-black text-emerald-300 mb-2 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                {isFr ? "Prochains jeudis · Ottawa" : "Next Thursdays · Ottawa"}
              </p>
              <div className="space-y-3">
                {linda.thursdays.map((thu) => (
                  <div key={thu.date} data-testid={`linda-thursday-${thu.date}`}>
                    <p className="text-white text-sm font-semibold mb-1.5">
                      {_fmtDayLabel(thu.date, isFr)}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {(thu.times || []).map((hhmm) => (
                        <button
                          key={`${thu.date}-${hhmm}`}
                          type="button"
                          onClick={() => handlePick(thu, hhmm, thu.variation_id)}
                          data-testid={`linda-slot-${thu.date}-${hhmm}`}
                          className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg bg-zinc-900/60 hover:bg-emerald-500/25 border border-emerald-400/40 hover:border-emerald-300 transition-all"
                        >
                          <Clock className="w-3 h-3 text-emerald-300" />
                          <span className="text-white text-sm font-bold tabular-nums">
                            {_fmtTime12(hhmm)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-zinc-500 text-[10px] text-center mt-4 leading-relaxed">
              {isFr
                ? "Linda commence à offrir Botox et Filler chaque jeudi à partir du 8 octobre 2026."
                : "Linda begins offering Botox & Filler every Thursday starting October 8, 2026."}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
